//hook
import { UserConsumer } from '../../hooks/context/UserContext';
import { NotificationConsumer } from '../../hooks/context/NotificationContext';
import { useApi } from '../../hooks/useApi';

//type
import { completedHostReqType, hostProfileType } from '../../api/completedRole/type/CompletedRoleType';

//api
import { PostCompletedRoleApi } from '../../api/completedRole/PostCompletedRoleApi';
import { GetProfileApi } from '../../api/profile/GetProfileApi';
import { UpdateCompletedRoleApi } from '../../api/completedRole/UpdateCompletedRoleApi';

//formik and yap
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

//mui
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

//translate
import { useTranslation } from 'react-i18next';
import i18n from '../../translate/i18n';

interface hostFieldProps {
    setOpenCompletedProfile: (open: boolean) => void
    hostProfile: hostProfileType,
    setHostProfile:(clientProfile: hostProfileType) => void
    hostCommercialRegistry: string,
    setHostCommercialRegistry: (hostCommercialRegistry: string) => void
    themeRole: string
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const storageURL = process.env.REACT_APP_STORAGE_BASE_URL;

const CompletedHostField = (
    {
    setOpenCompletedProfile, 
    hostProfile,
    setHostProfile,
    hostCommercialRegistry,
    setHostCommercialRegistry,
    themeRole
    }: hostFieldProps,
) => {
    const { t } = useTranslation();

    let {userData,setUserData} = UserConsumer()

    const {
        loading: postCompletedRoleLoading,
        error: postCompletedRoleError,
        HandlerApi: postCompletedRoleApi
    } = useApi(PostCompletedRoleApi, "Post");

    const {
        loading: updateCompletedRoleLoading,
        error: updateCompletedRoleError,
        HandlerApi: updateCompletedRoleApi
    } = useApi(UpdateCompletedRoleApi, "Post");

    //validation
    const hostSchema = Yup.object({
    company_name: Yup.string().when("is_company", {
        is: 1,
        then: (schema) => schema.required(t("company_name_required")),
        otherwise: (schema) => schema.notRequired(),
    }),

    commercial_registry: Yup.mixed().when("is_company", {
        is: 1,
        then: (schema) =>
        schema
            .required(t("commercial_registry_image_required"))
            .test(
            "fileType",
            t("invalid_file_type"),
            (value) => {
                if (!value) return false;
                if (typeof value === "string") return true;
                return ["image/jpeg", "image/png", "image/jpg"].includes(
                (value as File).type
                );
            }
            ),
        otherwise: (schema) => schema.notRequired(),
    }),
    });

    //convert url to file
    const convertUrlToFile = async (url: string) => {
        const response = await fetch(`${storageURL}/${url}`);
        const blob = await response.blob();
        const ext = url.split('.').pop() || 'png';
        const fileName = url.split('/').pop()?.split('.').slice(0, -1).join('') || 'file';
        return new File([blob], `${fileName}.${ext}`, { type: blob.type });
    };

    //submit
    const handleSubmit = async (values: completedHostReqType, theme:string)=> {
        let payload: completedHostReqType = {
            is_company: values.is_company,
        };
        if (values.is_company === 1) {
            payload.company_name = values.company_name;
            payload.commercial_registry = values.commercial_registry;
        }
        let result:any = ''
        console.log(payload)
        if(userData?.is_host) {
            if ('commercial_registry' in values && typeof values.commercial_registry === "string") {
                values.commercial_registry = await convertUrlToFile(values.commercial_registry);
            }
            result = await updateCompletedRoleApi(theme, payload, hostProfile?.id);
        }
        else {
            result = await postCompletedRoleApi(theme, payload);
        }
        if(result.code == 200 || result.code == 201) {
            setHostCommercialRegistry("")
            setOpenCompletedProfile(false)
            const resultprofile = await GetProfileApi();
            setUserData(resultprofile.data);
            setHostProfile({   
                id:0,
                user_id:0,
                is_company:0,
                company_name:"",
                commercial_registry:null,
                is_confirmed:false,
            })
        }
    }

    return (
        <div>
            <Formik
            enableReinitialize
            initialValues={{
                is_company: hostProfile?.is_company ?? 0,
                company_name: hostProfile?.company_name ?? "",
                commercial_registry: hostProfile?.commercial_registry ?? null,
            }}
            validationSchema={hostSchema}
            onSubmit={(values)=>handleSubmit(values, themeRole)}
            >
            {({ values,touched, errors,setFieldTouched, setFieldValue }) => (
                <Form>
                    <div>
                        <FormGroup>
                            <FormControlLabel
                            control={
                                <Switch
                                checked={values.is_company === 1}
                                onChange={(e) => {
                                    const isCompany = e.target.checked ? 1 : 0;
                                    setFieldValue("is_company", isCompany);

                                    if (isCompany === 0) {
                                    setFieldValue("company_name", "");
                                    setFieldValue("commercial_registry", null);
                                    setHostCommercialRegistry("");
                                    setFieldTouched("company_name", false);
                                    setFieldTouched("commercial_registry", false);
                                    } else {
                                    setFieldTouched("company_name", false);
                                    setFieldTouched("commercial_registry", false);
                                    }
                                }}
                                />
                            }
                            label={t("is_company")}
                            />
                        </FormGroup>
                        <Field
                            as={TextField}
                            name="company_name"
                            label={t("company_name")}
                            className={values.is_company === 1 ? "block sm:w-1/2 w-full !mt-2" : "!hidden"}
                            helperText={touched.company_name && errors.company_name ? errors.company_name : ""}
                            error={touched.company_name && Boolean(errors.company_name)}
                        />
                    </div>
                    <div className={values.is_company === 1 ? "block mt-5" : "hidden"}>
                        <Button
                            component="label"
                            className='!bg-paretnerColor !text-white w-fit !capitalize'
                            startIcon={<CloudUploadIcon />}
                        >
                            {t("commercial_registry_image")}
                            <VisuallyHiddenInput
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                                const file = event.target.files?.[0];
                                if (file) {
                                setFieldValue("commercial_registry", file);
                                setHostCommercialRegistry(URL.createObjectURL(file));
                                }
                            }}
                            />
                        </Button>
                        {/* Show selected image preview */}
                        {hostCommercialRegistry 
                            ? (
                                <img
                                src={hostCommercialRegistry}
                                alt="Back license preview"
                                className="mt-3 w-40 h-28 object-cover rounded-lg border"
                                />
                            ) 
                            : userData?.is_host && hostProfile?.commercial_registry ? (
                                // Show existing uploaded image from backend
                                <img
                                src={`${storageURL}/${hostProfile.commercial_registry}`}
                                alt="Back license uploaded"
                                className="mt-3 w-40 h-28 object-cover rounded-lg border"
                                />
                            ) : null
                        }
                        <p className="text-[12px] mt-2 text-red-500">
                            {touched.commercial_registry && errors.commercial_registry ? errors.commercial_registry : ""}
                        </p>
                    </div>
                    <hr className='mt-5'/>
                    <DialogActions className={i18n.language === "en" ? "!justify-start" : "!justify-start"}>
                        <Button className='w-full !bg-paretnerColor !text-white !rounded-full' type="submit">
                            {postCompletedRoleLoading || updateCompletedRoleLoading 
                            ? <CircularProgress className='w-1 h-1'/>
                            :t("confirm")
                            }
                        </Button>
                    </DialogActions>
                </Form>
            )}
            </Formik>
        </div>
    )
}

export default CompletedHostField